import { DEFAULT_TIMEZONE, SCHEDULE_DAYS, SCHEDULE_LIMITS, type ScheduleDay } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Icon, Input } from "@sagot/ui";
import { useWorkspaceDetails } from "../../../../session/workspace-context";
import type { CustomSchedule } from "../types";
import { addRange, rangeProblem, rangesOf, removeRange, updateRange } from "./schedule-model";

const S = "engage.workflows.builder.customSchedule";

interface DayRowProps {
  day: ScheduleDay;
  schedule: CustomSchedule;
  disabled: boolean;
  onChange: (schedule: CustomSchedule) => void;
}

function DayRow({ day, schedule, disabled, onChange }: DayRowProps) {
  const { t } = useTranslation();
  const name = t(`${S}.days.${day}`);
  const ranges = rangesOf(schedule, day);
  return (
    <li className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start" data-testid={`schedule-${day}`}>
      <span className="w-28 shrink-0 pt-2 text-sm font-semibold">{name}</span>
      <div className="min-w-0 flex-1 space-y-2">
        {ranges.length === 0 ? <p className="pt-2 text-xs text-fg-subtle">{t(`${S}.closed`)}</p> : null}
        {ranges.map((range, index) => {
          const problem = rangeProblem(ranges, index);
          const label = (part: string) => t(`${S}.rangeLabel`, { day: name, part: t(`${S}.${part}`), n: index + 1 });
          return (
            <div key={index}>
              <div className="flex flex-wrap items-center gap-2">
                <Input type="time" className="h-10 w-32" aria-label={label("from")} disabled={disabled} invalid={problem !== null} value={range.from} onChange={(e) => { onChange(updateRange(schedule, day, index, { from: e.target.value })); }} />
                <span aria-hidden="true" className="text-fg-subtle">–</span>
                <Input type="time" className="h-10 w-32" aria-label={label("to")} disabled={disabled} invalid={problem !== null} value={range.to} onChange={(e) => { onChange(updateRange(schedule, day, index, { to: e.target.value })); }} />
                <Button type="button" variant="ghost" size="sm" iconOnly disabled={disabled} aria-label={t(`${S}.removeRange`, { day: name, n: index + 1 })} onClick={() => { onChange(removeRange(schedule, day, index)); }}>
                  <Icon name="trash" />
                </Button>
              </div>
              {problem ? <p className="mt-1 text-xs font-semibold text-danger" role="alert">{t(`${S}.problems.${problem}`)}</p> : null}
            </div>
          );
        })}
        {ranges.length < SCHEDULE_LIMITS.rangesPerDay ? (
          <Button type="button" variant="ghost" size="sm" disabled={disabled} aria-label={t(`${S}.addRangeFor`, { day: name })} onClick={() => { onChange(addRange(schedule, day)); }}>
            <Icon name="plus" />
            {t(`${S}.addRange`)}
          </Button>
        ) : null}
      </div>
    </li>
  );
}

/** Weekly opening hours: per day any number of ranges (up to four); a day without ranges is closed. */
export function CustomScheduleEditor({ schedule, disabled, onChange }: { schedule: CustomSchedule; disabled: boolean; onChange: (schedule: CustomSchedule) => void }) {
  const { t } = useTranslation();
  const zone = useWorkspaceDetails().data?.timezone ?? DEFAULT_TIMEZONE;
  return (
    <div className="rounded-md border border-line p-3" data-testid="custom-schedule">
      <p className="text-xs text-fg-subtle">{t(`${S}.hint`)}</p>
      <p className="mt-1 text-xs font-semibold text-fg-muted" data-testid="schedule-zone">{t(`${S}.zone`, { zone })}</p>
      <ul className="mt-1 divide-y divide-line" aria-label={t(`${S}.title`)}>
        {SCHEDULE_DAYS.map((day) => <DayRow key={day} day={day} schedule={schedule} disabled={disabled} onChange={onChange} />)}
      </ul>
    </div>
  );
}
